import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { IssueReport } from '../../api/types';
import { useUpdateIssueReportMutation } from '../../api/issueReport/issueReportApiSlice';
import { useAuth0 } from '../../react-auth0-spa';
import CustomButton from '../../components/CustomButton';
import {
    enqueueErrorSnackbar,
    enqueueSuccessSnackbar,
} from '../../action/snackbarCommands';

interface EditIssueReportDialogProps {
    open: boolean;
    onClose: () => void;
    issueReport: IssueReport;
    onUpdated?: () => void;
}

const EditIssueReportDialog = (props: EditIssueReportDialogProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [issueReportStatus, setIssueReportStatus] = useState<string>(props.issueReport.state as string);
    const [comment, setComment] = useState<string>('');
    const { user } = useAuth0();
    const [updateIssueReport, { isLoading: isUpdating }] = useUpdateIssueReportMutation();

    const resetDialog = () => {
        setIssueReportStatus(props.issueReport.state);
        setComment('');
    };

    useEffect(resetDialog, []);
    useEffect(resetDialog, [props.issueReport]);

    const handleChangeStatusClick = async () => {
        if (!user) return;
        try {
            // `.unwrap()` surfaces a real mutation failure as a rejected
            // promise so we can distinguish a genuine save error from
            // downstream side-effects (e.g. the post-save refetch failing
            // because the station hardware is unreachable, which is
            // independent of whether the save itself succeeded).
            await updateIssueReport({
                id: props.issueReport.id,
                comment,
                state: issueReportStatus,
                profileInfo: {
                    name: user.name,
                    sub: user.sub,
                    nickname: user.nickname,
                    email: user.email,
                    picture: user.picture,
                },
            }).unwrap();
            dispatch(
                enqueueSuccessSnackbar({
                    message: t('issue_reports.update_success'),
                }),
            );
            if (props.onUpdated) {
                props.onUpdated();
            }
            props.onClose();
        } catch (error) {
            dispatch(
                enqueueErrorSnackbar({
                    message: t('issue_reports.update_error'),
                }),
            );
        }
    };

    if (!props.open) return null;

    return (
        <div className={'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40'}>
            <div className={'bg-white rounded-xl shadow-lg w-full max-w-md mx-4'}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleChangeStatusClick();
                    }}
                >
                    <div className={'px-6 py-4'}>
                        <fieldset>
                            <legend className={'block text-sm font-medium mb-2'}>{t('issue_reports.change_status') || 'Issue status'}</legend>
                            <div className={'space-y-2 mb-4'}>
                                <label className={'flex items-center gap-2 cursor-pointer'}>
                                    <input
                                        type={'radio'}
                                        name={'status'}
                                        value={'open'}
                                        checked={issueReportStatus === 'open'}
                                        onChange={() => setIssueReportStatus('open')}
                                        className={'accent-cyan-500'}
                                    />
                                    <span>{t('issue_reports.card.open')}</span>
                                </label>
                                <label className={'flex items-center gap-2 cursor-pointer'}>
                                    <input
                                        type={'radio'}
                                        name={'status'}
                                        value={'in progress'}
                                        checked={issueReportStatus === 'in progress'}
                                        onChange={() => setIssueReportStatus('in progress')}
                                        className={'accent-cyan-500'}
                                    />
                                    <span>{t('issue_reports.card.in_progress')}</span>
                                </label>
                                <label className={'flex items-center gap-2 cursor-pointer'}>
                                    <input
                                        type={'radio'}
                                        name={'status'}
                                        value={'on hold'}
                                        checked={issueReportStatus === 'on hold'}
                                        onChange={() => setIssueReportStatus('on hold')}
                                        className={'accent-cyan-500'}
                                    />
                                    <span>{t('issue_reports.card.on_hold')}</span>
                                </label>
                                <label className={'flex items-center gap-2 cursor-pointer'}>
                                    <input
                                        type={'radio'}
                                        name={'status'}
                                        value={'done'}
                                        checked={issueReportStatus === 'done'}
                                        onChange={() => setIssueReportStatus('done')}
                                        className={'accent-cyan-500'}
                                    />
                                    <span>{t('issue_reports.card.resolved')}</span>
                                </label>
                            </div>
                        </fieldset>
                        <textarea
                            className={
                                'w-full mt-2 p-3 border border-gray-300 rounded-lg resize-none ' +
                                'focus:outline-none focus:ring-2 focus:ring-cyan-400 text-base placeholder-gray-400'
                            }
                            rows={4}
                            placeholder={t('issue_reports.comment_placeholder')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>
                    <div className={'flex justify-end gap-3 px-6 pb-6'}>
                        <button
                            type={'button'}
                            className={'h-12 px-6 rounded-lg border border-black bg-white text-black font-semibold text-base hover:bg-gray-100 transition'}
                            onClick={props.onClose}
                        >
                            {t('common.cancel')}
                        </button>
                        <CustomButton
                            title={isUpdating ? t('common.loading') : t('common.save')}
                            onClick={handleChangeStatusClick}
                            disabled={isUpdating}
                            isFilled={true}
                            fullWidth={false}
                        /> 
                        {/* <button
                            type={'submit'}
                            className={'h-12 px-6 rounded-lg bg-cyan-400 text-white font-semibold text-base hover:bg-cyan-500 transition'}
                            disabled={isUpdating}
                        >
                            {isUpdating ? t('common.loading') : t('common.save')}
                        </button>  */}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditIssueReportDialog;
